// 2026-04-22 booking.com-style language switcher rewrite.
//
// Previous model: 5 TLDs × locale-in-path (`favotrip.be/nl`, `favotrip.be/fr`),
// cross-domain market switcher, per-domain currency (but UK = GBP).
//
// New model per Kartik 11:15 AM + Dennis clarification:
//   - All 5 TLDs serve the same app; visitors stay on whatever domain they
//     landed on. No cross-domain redirects.
//   - Content language is decided by a 3-step chain:
//       1. favotrip_lang cookie (user's explicit choice via the header
//          dropdown) — always wins.
//       2. Accept-Language header — if the first supported language (NL/EN/
//          DE/FR) is in it, use that.
//       3. Fall back to the domain's local default (.nl → NL, .be → NL,
//          .de → DE, .fr → FR, .co.uk → EN).
//   - Currency is tied to the domain (.co.uk = GBP, everything else = EUR).
//     Simpler than a cookie-based currency toggle and removes a footgun on
//     the Stripe charge path.
//   - Header dropdown lets the visitor override the language; a click writes
//     the cookie and reloads. Domain stays the same throughout.

// 2026-04-26: cookie name bumped from `favotrip_lang` to `favotrip_lang_v2`.
// The previous cookie was being auto-written on every page-load (see
// providers.js syncLanguagePreference), which polluted the preference for
// any user who landed on a non-NL TLD via Google. After the fix, the cookie
// is only written on explicit ?lang= action, but every existing visitor still
// has a stale `favotrip_lang` value in their browser. Bumping the name forces
// a clean slate for the entire fleet — anyone who genuinely wants a non-default
// language can re-pick it via the header dropdown.
// Edge-safe, dependency-free catalog slug-i18n map. Used to auto-localize the
// hreflang alternates + canonical per market (see resolveAlternatePathname).
import { localizePath, toPhysicalPath } from "./slug-paths";

export const LANG_COOKIE = "favotrip_lang_v2";
/** Query param that overrides the content language (e.g. ?ft-lang=de); acting on it writes LANG_COOKIE and reloads. */
export const LANG_QUERY = "ft-lang";

// Legacy cookie names — still read once at first request so existing visitors
// don't get a visible language flip on next page-view. Stripped to locale
// from the old "market" format (e.g. "nl-be" → "nl").
//
// `favotrip_lang` (no _v2 suffix) is the polluted cookie described above and
// is intentionally excluded from the legacy fallback chain — we want it to
// be silently ignored. Visitors get the domain default until they make an
// explicit pick.
export const LEGACY_MARKET_COOKIE = "favotrip_market_preference";

// Kept for backwards-compatibility with anywhere the old cookie name leaks.
export const SITE_PREFERENCE_COOKIE = LANG_COOKIE;
/** Back-compat alias of LANG_QUERY for older call sites. */
export const SITE_PREFERENCE_QUERY = LANG_QUERY;

/** Names of the x-favotrip-* request headers the middleware sets so server components can read the resolved market/locale/currency/domain. */
export const SITE_CONTEXT_HEADERS = {
  market: "x-favotrip-market",
  locale: "x-favotrip-locale",
  currency: "x-favotrip-currency",
  domain: "x-favotrip-domain",
  baseUrl: "x-favotrip-base-url",
  pathname: "x-favotrip-pathname",
  localePrefix: "x-favotrip-locale-prefix", // kept, always empty string now
  staging: "x-favotrip-staging",
};

/** The 5 content locales the site supports (nl/en/de/fr/pl); EN is the source, the others fall back to it. */
export const SUPPORTED_LOCALES = ["nl", "en", "de", "fr", "pl"];

// Per-locale display metadata used by the header language dropdown. The
// `shortLabel` shows as the compact trigger; `nativeLabel` is the full name
// shown inside the dropdown panel; `flag` is the emoji rendered next to it.
export const LANGUAGE_OPTIONS = [
  { code: "nl", nativeLabel: "Nederlands", shortLabel: "NL", flag: "\u{1F1F3}\u{1F1F1}" },
  { code: "en", nativeLabel: "English",    shortLabel: "EN", flag: "\u{1F1EC}\u{1F1E7}" },
  { code: "de", nativeLabel: "Deutsch",    shortLabel: "DE", flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "fr", nativeLabel: "Français",   shortLabel: "FR", flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "pl", nativeLabel: "Polski",     shortLabel: "PL", flag: "\u{1F1F5}\u{1F1F1}" },
];

// The six TLDs we serve. Each maps to a default locale (the fallback when
// the visitor's browser language is not supported) and a currency.
export const DOMAIN_CONFIGS = [
  { domain: "favotrip.nl",    defaultLocale: "nl", currency: "EUR", label: "Nederland" },
  { domain: "favotrip.be",    defaultLocale: "nl", currency: "EUR", label: "België" },
  { domain: "favotrip.de",    defaultLocale: "de", currency: "EUR", label: "Deutschland" },
  { domain: "favotrip.fr",    defaultLocale: "fr", currency: "EUR", label: "France" },
  { domain: "favotrip.co.uk", defaultLocale: "en", currency: "GBP", label: "United Kingdom" },
  { domain: "favotrip.pl",    defaultLocale: "pl", currency: "PLN", label: "Polska" },
];

export const DEFAULT_DOMAIN = "favotrip.nl";
export const DEFAULT_LOCALE = "nl";

// Kept so old callers that reference MARKET_OPTIONS keep compiling. The
// `key` values mirror the old 6-market identifiers (nl-nl / nl-be / fr-be /
// de-de / fr-fr / en-gb) but now represent the (default-locale, domain)
// pairs instead of locale-path routes.
export const MARKET_OPTIONS = [
  {
    key: "nl-nl", locale: "nl", hreflang: "nl-NL", currency: "EUR", domain: "favotrip.nl",
    label: "Nederland", nativeLabel: "Nederlands", flag: "\u{1F1F3}\u{1F1F1}",
    shortLabel: "NL", pathPrefix: "",
  },
  {
    key: "nl-be", locale: "nl", hreflang: "nl-BE", currency: "EUR", domain: "favotrip.be",
    label: "België", nativeLabel: "Nederlands", flag: "\u{1F1E7}\u{1F1EA}",
    shortLabel: "BE", pathPrefix: "",
  },
  {
    key: "fr-be", locale: "fr", hreflang: "fr-BE", currency: "EUR", domain: "favotrip.be",
    label: "Belgique", nativeLabel: "Français", flag: "\u{1F1E7}\u{1F1EA}",
    shortLabel: "BE", pathPrefix: "",
    // 2026-07-19 i18n/SEO audit (8-safe): favotrip.be's domain default is nl
    // (DOMAIN_CONFIGS above) and resolveLocale() intentionally does NOT
    // consult Accept-Language on cookie-less requests (see the 2026-05-22
    // comment on resolveLocale below), so a cookie-less crawler hitting
    // this hreflang="fr-BE" URL is served the NL page, not French. There is
    // no indexable French surface on .be today. This entry stays a fully
    // real, working MARKET_OPTIONS member for actual visitors who pick
    // French via the language-switcher cookie (see
    // middleware-localized-slug.regression.test.js "fr-BE market... .be + fr
    // cookie"; also consumed by payment-methods.js, redeem-paths.js,
    // withdrawal-paths.js, structured-data.js, popularity-fetch.js keyed by
    // this exact marketKey). Removing the entry outright would silently
    // regress those real, already-shipped per-session behaviors. Only
    // hreflangIndexable is set to false, which excludes fr-BE from the
    // crawler-facing <link rel=alternate hreflang> set built by
    // getAlternateLanguageUrls below, without touching resolveMarketKey/
    // getMarketConfig or any real-visitor code path. Bring it back by
    // deleting this flag (or flipping it to true) once a real, indexable
    // French page exists on favotrip.be.
    hreflangIndexable: false,
  },
  {
    key: "de-de", locale: "de", hreflang: "de-DE", currency: "EUR", domain: "favotrip.de",
    label: "Deutschland", nativeLabel: "Deutsch", flag: "\u{1F1E9}\u{1F1EA}",
    shortLabel: "DE", pathPrefix: "",
  },
  {
    key: "fr-fr", locale: "fr", hreflang: "fr-FR", currency: "EUR", domain: "favotrip.fr",
    label: "France", nativeLabel: "Français", flag: "\u{1F1EB}\u{1F1F7}",
    shortLabel: "FR", pathPrefix: "",
  },
  {
    key: "en-gb", locale: "en", hreflang: "en-GB", currency: "GBP", domain: "favotrip.co.uk",
    label: "United Kingdom", nativeLabel: "English", flag: "\u{1F1EC}\u{1F1E7}",
    shortLabel: "UK", pathPrefix: "",
  },
  {
    key: "pl-pl", locale: "pl", hreflang: "pl-PL", currency: "PLN", domain: "favotrip.pl",
    label: "Polska", nativeLabel: "Polski", flag: "\u{1F1F5}\u{1F1F1}",
    shortLabel: "PL", pathPrefix: "",
  },
];

export const DEFAULT_MARKET_KEY = "nl-nl";

function normalizePathname(pathname = "/") {
  if (!pathname) return "/";
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return normalized === "/" ? normalized : normalized.replace(/\/+$/, "") || "/";
}

/** Normalise a hostname to lowercase, trimmed, host-only (strips port/path via URL parsing; handles IPv6 brackets). */
export function normalizeHostname(hostname = "") {
  const normalized = String(hostname).trim().toLowerCase();
  if (!normalized) return "";

  try {
    return new URL(`http://${normalized}`).hostname;
  } catch {
    if (normalized.startsWith("[")) {
      const closingBracket = normalized.indexOf("]");
      if (closingBracket !== -1) {
        return normalized.slice(0, closingBracket + 1);
      }
    }
    return normalized.split(":")[0];
  }
}

export function isStagingHost(hostname = "") {
  return normalizeHostname(hostname).startsWith("staging.");
}

function stripWwwAndStaging(host = "") {
  return host.replace(/^www\./, "").replace(/^staging\./, "");
}

function resolveDomainConfig(hostname = "") {
  const host = stripWwwAndStaging(normalizeHostname(hostname));
  return (
    DOMAIN_CONFIGS.find((config) => config.domain === host) ||
    DOMAIN_CONFIGS[0]
  );
}

/**
 * The locale a cookie-less / query-less visitor on this host receives — i.e.
 * the host's TLD default (.nl/.be → nl, .de → de, .fr → fr, .co.uk → en,
 * .pl → pl). `www.` and `staging.` prefixes are stripped, so both
 * `www.favotrip.nl` and a bare `favotrip.nl` resolve to `nl`.
 *
 * Used by the middleware edge-cache gate: only a render whose locale equals
 * this default is byte-identical to the cookie-less render and therefore
 * safe to put in Cloudflare's shared (cookie-blind) HTML cache. Any render
 * whose locale was overridden away from this default must stay private, or
 * it poisons the shared cache for every cookie-less visitor (incident
 * 2026-06-25: www.favotrip.nl/ served a cached EN page while origin was NL).
 */
export function getDomainDefaultLocale(hostname = "") {
  return resolveDomainConfig(hostname).defaultLocale;
}

// Codex 2026-04-22: all five public TLDs 301 the apex to the `www.` host
// (`curl -sI https://favotrip.de/` → `location: https://www.favotrip.de/`).
// We were emitting the apex in canonicals + hreflang alternates, so
// Google was crawling URLs that always redirect. Prefer the final
// public host that returns 200. Staging intentionally keeps the
// `staging.` prefix — that host isn't behind the www redirect.
function getDomainHost(domain, { staging = false } = {}) {
  if (staging) return `staging.${domain}`;
  return `www.${domain}`;
}

// Resolve the market config whose (locale, domain) matches the given locale +
// TLD. Falls back to the domain's default-locale market if the requested
// pair has no entry (e.g. English on .de — that composite isn't listed in
// MARKET_OPTIONS and we just surface the .de default market).
export function getMarketConfig(key = DEFAULT_MARKET_KEY) {
  return MARKET_OPTIONS.find((market) => market.key === key) || MARKET_OPTIONS[0];
}

function resolveMarketKey(locale, domain) {
  const exact = MARKET_OPTIONS.find((m) => m.locale === locale && m.domain === domain);
  if (exact) return exact.key;
  const domainDefault = MARKET_OPTIONS.find((m) => m.domain === domain);
  return domainDefault ? domainDefault.key : DEFAULT_MARKET_KEY;
}

export function getCurrencyConfig(currency = "EUR") {
  if (currency === "GBP") {
    return {
      code: "GBP",
      symbol: "£",
      locale: "en-GB",
      symbolSpacing: "",
      trailingWholeSuffix: "",
    };
  }

  if (currency === "PLN") {
    // Polish złoty: symbol "zł" goes AFTER the amount with a space
    // ("250 zł"), comma decimal separator, no minteken suffix. The
    // symbol-after placement is handled in format-price.js (currency-keyed,
    // like GBP), not via the EUR symbolAfter convention.
    return {
      code: "PLN",
      symbol: "zł",
      locale: "pl-PL",
      symbolSpacing: " ",
      trailingWholeSuffix: "",
    };
  }

  if (currency === "USD") {
    // USD is not a Favotrip retail market currency, but the Musement
    // experiences storefront surfaces supplier prices in the provider's own
    // currency (the sandbox returns USD). Render "$49.90": symbol before the
    // amount, no spacing, dot decimal separator (handled currency-keyed in
    // format-price.js, like GBP), no minteken suffix.
    return {
      code: "USD",
      symbol: "$",
      locale: "en-US",
      symbolSpacing: "",
      trailingWholeSuffix: "",
    };
  }

  return {
    code: "EUR",
    symbol: "€",
    locale: "nl-NL",
    symbolSpacing: " ",
    trailingWholeSuffix: ",-",
  };
}

// Kept for back-compat; no more locale-prefixed paths, so this is an identity
// function that just normalises slashes.
export function stripLocalePrefix(pathname = "/") {
  return normalizePathname(pathname);
}

// Kept for back-compat. The pathPrefix on every market is now "", so this
// returns the input unchanged except for slash normalisation.
export function applyMarketPrefix(pathname = "/") {
  return normalizePathname(pathname);
}

/** Build an absolute URL for a market's domain + path (with optional search string and staging host). */
export function buildMarketUrl(marketKey, pathname = "/", { search = "", staging = false } = {}) {
  const market = getMarketConfig(marketKey);
  const url = new URL(`https://${getDomainHost(market.domain, { staging })}`);
  url.pathname = normalizePathname(pathname);
  url.search = search || "";
  return url.toString();
}

function resolveAlternatePathname(marketKey, pathname, alternatePathnamesByMarket = {}) {
  // Explicit per-market override wins (e.g. the redeem pages pass
  // REDEEM_ALTERNATE_PATHS). Otherwise auto-localize the page's path for the
  // target market's locale via the central slug map: the incoming pathname is
  // first mapped to its physical Dutch route (whatever locale it arrived in),
  // then localized for the target market. Catalog routes get per-market slugs
  // (/account/boekingen <-> /konto/buchungen <-> /compte/reservations);
  // non-catalog routes (/packages, ...) are returned unchanged, so canonical +
  // hreflang stay byte-identical to today for those pages.
  if (alternatePathnamesByMarket[marketKey]) {
    return alternatePathnamesByMarket[marketKey];
  }
  const market = MARKET_OPTIONS.find((m) => m.key === marketKey);
  const locale = market ? market.locale : DEFAULT_LOCALE;
  return localizePath(toPhysicalPath(pathname), locale);
}

export function getAlternateLanguageUrls(
  pathname = "/",
  { search = "", staging = false, alternatePathnamesByMarket = {} } = {},
) {
  const urls = {};

  for (const market of MARKET_OPTIONS) {
    // 2026-07-19 i18n/SEO audit (8-safe): skip markets that aren't a real,
    // indexable surface for crawlers (currently only fr-BE, see the
    // hreflangIndexable comment on that MARKET_OPTIONS entry above). This
    // only affects the hreflang <link> set; the market stays fully usable
    // everywhere else (real-visitor locale resolution, payment methods,
    // redeem/withdrawal paths, ...).
    if (market.hreflangIndexable === false) continue;
    urls[market.hreflang] = buildMarketUrl(
      market.key,
      resolveAlternatePathname(market.key, pathname, alternatePathnamesByMarket),
      { search, staging },
    );
  }

  urls["x-default"] = buildMarketUrl(
    DEFAULT_MARKET_KEY,
    resolveAlternatePathname(DEFAULT_MARKET_KEY, pathname, alternatePathnamesByMarket),
    { search, staging },
  );
  return urls;
}

export function getPathnameForMetadata(pathname = "/") {
  return normalizePathname(pathname);
}

// ───────────────────────────────────────────────────────────────
// Language resolution — 3-step chain
// ───────────────────────────────────────────────────────────────

function parseAcceptLanguage(header = "") {
  if (!header) return [];
  return header
    .split(",")
    .map((chunk) => {
      // Accept-Language may include more than one parameter after the tag
      // (`de-CH;q=0.8;region=CH`); we only care about q, so scan all
      // parameters instead of taking just the first segment.
      const parts = chunk.trim().split(";");
      const tag = (parts.shift() || "").toLowerCase();
      let q = 1;
      for (const part of parts) {
        const trimmed = part.trim();
        if (trimmed.startsWith("q=")) {
          const parsed = parseFloat(trimmed.slice(2));
          if (Number.isFinite(parsed)) q = parsed;
        }
      }
      return { tag, q };
    })
    // Codex 2026-04-22: RFC 7231 says `q=0` means "explicitly not acceptable".
    // We used to let a `de;q=0` entry win the sort because the sort was
    // stable and the filter only dropped empty tags, so a browser sending
    // `en-GB,de;q=0` on `.co.uk` would still be resolved to German. Drop
    // entries with q<=0 before sorting.
    .filter((entry) => entry.tag && entry.q > 0)
    .sort((a, b) => b.q - a.q)
    .map((entry) => entry.tag.split("-")[0]);
}

function pickSupportedFromBrowser(acceptLanguage = "") {
  for (const code of parseAcceptLanguage(acceptLanguage)) {
    if (SUPPORTED_LOCALES.includes(code)) return code;
  }
  return null;
}

// Normalise whatever the legacy market cookie held into a bare locale.
function legacyMarketCookieToLocale(rawCookieValue) {
  if (!rawCookieValue) return null;
  const raw = String(rawCookieValue).toLowerCase();
  const head = raw.split("-")[0];
  return SUPPORTED_LOCALES.includes(head) ? head : null;
}

export function resolveLocale({
  cookieLang = null,
  legacyMarketCookie = null,
  acceptLanguage = "",
  hostname = "",
} = {}) {
  if (cookieLang && SUPPORTED_LOCALES.includes(cookieLang.toLowerCase())) {
    return cookieLang.toLowerCase();
  }
  const legacy = legacyMarketCookieToLocale(legacyMarketCookie);
  if (legacy) return legacy;

  // 2026-05-22: Accept-Language is intentionally NOT consulted on
  // cookie-less requests. The site is TLD-segmented (favotrip.de = de,
  // favotrip.fr = fr, etc.), so the host already carries the locale
  // signal. Reading Accept-Language meant the same URL on the same
  // host returned different responses depending on browser language,
  // which broke Cloudflare's HTML cache (CF does not vary by
  // Accept-Language by default and ignores the Vary hint on the free
  // plan). Customer-visible symptom: favotrip.de/packages/wellness
  // served EN content after a synthetic monitor populated the cache.
  // Users who want a non-default locale set it via the language switcher
  // (writes the LANG_COOKIE), which IS honoured above. Reference: the
  // unused acceptLanguage parameter is kept on the signature so any
  // future opt-in (e.g. cookie-paired, after consent) can reintroduce
  // it without a breaking API change.
  void acceptLanguage;

  const domainConfig = resolveDomainConfig(hostname);
  return domainConfig.defaultLocale;
}

// ───────────────────────────────────────────────────────────────
// Site-context resolver (middleware entry point)
// ───────────────────────────────────────────────────────────────

export function resolveSiteContext({
  hostname = "",
  pathname = "/",
  cookieLang = null,
  legacyMarketCookie = null,
  acceptLanguage = "",
  preferredLocale = null,
  search = "",
} = {}) {
  const normalizedHost = normalizeHostname(hostname);
  const staging = isStagingHost(normalizedHost);
  const normalizedPath = normalizePathname(pathname);

  const domainConfig = resolveDomainConfig(normalizedHost);
  const locale = preferredLocale && SUPPORTED_LOCALES.includes(preferredLocale)
    ? preferredLocale
    : resolveLocale({
        cookieLang,
        legacyMarketCookie,
        acceptLanguage,
        hostname: normalizedHost,
      });

  const marketKey = resolveMarketKey(locale, domainConfig.domain);
  const market = getMarketConfig(marketKey);
  const baseUrl = `https://${getDomainHost(domainConfig.domain, { staging })}`;
  const languageOption =
    LANGUAGE_OPTIONS.find((opt) => opt.code === locale) || LANGUAGE_OPTIONS[0];

  return {
    // Primary new fields
    locale,
    currency: domainConfig.currency,
    domain: domainConfig.domain,
    baseUrl,
    hostname: normalizedHost,
    staging,
    pathname: normalizedPath,
    search,

    // Back-compat: many callers still read these off siteContext / market.
    key: marketKey,
    hreflang: market.hreflang,
    label: domainConfig.label,
    nativeLabel: languageOption.nativeLabel,
    flag: languageOption.flag,
    shortLabel: languageOption.shortLabel,
    pathPrefix: "",
    internalPathname: normalizedPath,
    visiblePathname: normalizedPath,
    localePrefix: "",
    redirectPathname: null,
  };
}

/** Build the canonical + hreflang alternates for a page: self-canonical per TLD, each locale pointing at the TLD whose default it is. */
export function buildPageAlternates(
  pathname = "/",
  siteContext,
  { search = "", alternatePathnamesByMarket = {} } = {},
) {
  // Self-canonical per domain: the URL the user is on is the canonical for
  // that TLD. Hreflang alternates point each locale at the TLD whose default
  // locale matches it, so Google can serve the right national version.
  const canonical = buildMarketUrl(
    siteContext.key,
    resolveAlternatePathname(
      siteContext.key,
      pathname,
      alternatePathnamesByMarket,
    ),
    {
      search,
      staging: siteContext.staging,
    },
  );

  return {
    canonical,
    languages: getAlternateLanguageUrls(pathname, {
      search,
      staging: siteContext.staging,
      alternatePathnamesByMarket,
    }),
  };
}
